import React, { useState } from 'react';
import { MdAdd, MdDelete, MdUpload, MdDownload } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { insertarCotizacion, verSimulacionOpe } from '../api/apiConfig';

const SimuladorOperaciones = () => {
  const { user: userData } = useUser();
  const navigate = useNavigate();
  
  // Debug: Ver valores de userData
  console.log('userData completo:', userData);
  console.log('Todas las propiedades de userData:', Object.keys(userData || {}));
  console.log('userData expandido:', JSON.stringify(userData, null, 2));
  
  const [formData, setFormData] = useState({
    nombreCliente: '',
    rutCliente: '',
    fechaOperacion: '',
    tasaInteres: '',
    porcentajeAnticipo: '',
    comision: '',
    gastosOperacionales: '',
    otrosGastos: ''
  });

  const [facturas, setFacturas] = useState([{
    numeroFactura: '',
    deudor: '',
    fechaVencimiento: '',
    montoFactura: ''
  }]);

  const [csvData, setCsvData] = useState([]);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFacturaChange = (index, field, value) => {
    const newFacturas = [...facturas];
    newFacturas[index][field] = value;
    setFacturas(newFacturas);
  };

  const addFactura = () => {
    setFacturas([...facturas, {
      numeroFactura: '',
      deudor: '',
      fechaVencimiento: '',
      montoFactura: ''
    }]);
  };

  const removeFactura = (index) => {
    if (facturas.length > 1) {
      setFacturas(facturas.filter((_, i) => i !== index));
    }
  };

  const downloadCsvPlanilla = () => {
    const headers = 'NUMERO_FACTURA;DEUDOR;FECHA_VENCIMIENTO;MONTO_FACTURA';
    const ejemplo = 'EJEMPLO001;Deudor Ejemplo S.A.;2025-12-31;150000';
    const contenido = [headers, ejemplo].join('\n');
    const blob = new Blob(['\ufeff' + contenido], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'planilla_facturas_simulador.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) return;

      const headers = lines[0].split(';').map(h => h.trim());
      const expectedHeaders = ['NUMERO_FACTURA', 'DEUDOR', 'FECHA_VENCIMIENTO', 'MONTO_FACTURA'];
      
      const hasAllHeaders = expectedHeaders.every(header => headers.includes(header));
      if (!hasAllHeaders) return;

      const convertDateFormat = (dateStr) => {
        if (!dateStr) return '';
        // Si viene en formato DD/MM/YYYY, convertir a YYYY-MM-DD
        if (dateStr.includes('/')) {
          const [day, month, year] = dateStr.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateStr;
      };

      const data = lines.slice(1).map(line => {
        const values = line.split(';').map(v => v.trim());
        const row = {};
        headers.forEach((header, index) => {
          if (expectedHeaders.includes(header)) {
            row[header] = values[index] || '';
          }
        });
        return {
          numeroFactura: row.NUMERO_FACTURA,
          deudor: row.DEUDOR,
          fechaVencimiento: convertDateFormat(row.FECHA_VENCIMIENTO),
          montoFactura: row.MONTO_FACTURA
        };
      });

      setCsvData(data);
      setFacturas(data);
      setShowCsvUpload(false);
    };
    
    reader.readAsText(file, 'ISO-8859-1');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombreCliente.trim()) {
      newErrors.nombreCliente = 'Nombre Cliente es obligatorio';
    }
    
    if (!formData.rutCliente.trim()) {
      newErrors.rutCliente = 'RUT Cliente es obligatorio';
    }
    
    if (!formData.fechaOperacion) {
      newErrors.fechaOperacion = 'Fecha operación es obligatoria';
    }
    
    if (!formData.tasaInteres.trim()) {
      newErrors.tasaInteres = 'Tasa de interés es obligatoria';
    }
    
    if (!formData.porcentajeAnticipo.trim()) {
      newErrors.porcentajeAnticipo = 'Porcentaje de anticipo es obligatorio';
    }
    
    if (!formData.comision.trim()) {
      newErrors.comision = 'Comisión es obligatoria';
    }
    
    if (!formData.gastosOperacionales.trim()) {
      newErrors.gastosOperacionales = 'Gastos operacionales son obligatorios';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setLoading(true);
      setMessage('');
      
      try {
        const cotizacionData = {
          cotizacion: {
            NOMBRE_CLIENTE: formData.nombreCliente,
            RUT_CLIENTE: formData.rutCliente,
            FECHA_OPE: formData.fechaOperacion,
            TASA: parseFloat(formData.tasaInteres),
            ANTICIPO: parseFloat(formData.porcentajeAnticipo),
            COMISION: parseFloat(formData.comision),
            GASTOS: parseFloat(formData.gastosOperacionales),
            GASTOS_OTROS: formData.otrosGastos ? parseFloat(formData.otrosGastos) : 0,
            EJECUTIVO: userData.ejecutivo_sistema_factoring,
            EQUIPO: userData.grupo
          },
          doctos: facturas.map(factura => ({
            NUM_FACTURA: factura.numeroFactura,
            DEUDOR: factura.deudor,
            FECHA_VENCIMIENTO: factura.fechaVencimiento,
            MONTO_FACTURA: parseFloat(factura.montoFactura) || 0
          }))
        };
        
        // 1. Insertar cotización
        console.log('Insertando cotización...');
        const insertResponse = await insertarCotizacion(cotizacionData);
        console.log('Respuesta insertar cotización:', insertResponse);
        
        const idCotizacion = insertResponse.body ? JSON.parse(insertResponse.body).id_cotizacion : insertResponse.id_cotizacion;
        console.log('ID Cotización obtenido:', idCotizacion);
        
        if (!idCotizacion) {
          throw new Error('No se pudo obtener el ID de la cotización');
        }
        
        // 2. Obtener simulación
        console.log('Obteniendo simulación para ID:', idCotizacion);
        const simulacionResponse = await verSimulacionOpe(idCotizacion);
        console.log('Respuesta simulación:', simulacionResponse);
        
        // Manejar diferentes formatos de respuesta
        let simulacionData;
        if (simulacionResponse.body) {
          simulacionData = typeof simulacionResponse.body === 'string' 
            ? JSON.parse(simulacionResponse.body) 
            : simulacionResponse.body;
        } else {
          simulacionData = simulacionResponse;
        }
        
        console.log('Datos de simulación procesados:', simulacionData);
        
        if (!simulacionData || (!simulacionData.detalle_documentos && !simulacionData.resumen_operacion)) {
          throw new Error('No se recibieron datos válidos de la simulación');
        }
        
        // 3. Navegar a página de resultados
        console.log('Navegando a página de resultados...');
        navigate('/resultado-simulacion', {
          state: { simulacionData, idCotizacion }
        });
        
      } catch (error) {
        setMessage(`Error al procesar simulación: ${error.message}`);
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Simulador de operaciones</h1>
      </div> 

      <form onSubmit={handleSubmit}>
        {message && (
          <div className={`mb-4 p-3 rounded ${messageType === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
            {message}
          </div>
        )}
        {/* Sección Datos Cliente */}
        <div className="seccion-datos mb-6">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
            Datos Cliente
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Nombre Cliente *
              </label>
              <input
                type="text"
                name="nombreCliente"
                value={formData.nombreCliente}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.nombreCliente ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingrese nombre del cliente"
              />
              {errors.nombreCliente && (
                <p className="text-red-500 text-sm mt-1">{errors.nombreCliente}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                RUT Cliente *
              </label>
              <input
                type="text"
                name="rutCliente"
                value={formData.rutCliente}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.rutCliente ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: 12345678-9"
              />
              {errors.rutCliente && (
                <p className="text-red-500 text-sm mt-1">{errors.rutCliente}</p>
              )}
            </div>
          </div>
        </div>

        {/* Sección Datos Operación */}
        <div className="seccion-datos mb-6">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
            Datos Operación
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Fecha operación *
              </label>
              <input
                type="date"
                name="fechaOperacion"
                value={formData.fechaOperacion}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.fechaOperacion ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.fechaOperacion && (
                <p className="text-red-500 text-sm mt-1">{errors.fechaOperacion}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                Tasa de interés * (%)
              </label>
              <input
                type="number"
                name="tasaInteres"
                value={formData.tasaInteres}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.tasaInteres ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: 2.5"
                step="0.01"
              />
              {errors.tasaInteres && (
                <p className="text-red-500 text-sm mt-1">{errors.tasaInteres}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                Porcentaje de anticipo * (%)
              </label>
              <input
                type="number"
                name="porcentajeAnticipo"
                value={formData.porcentajeAnticipo}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.porcentajeAnticipo ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: 80"
                step="0.01"
                min="0"
                max="100"
              />
              {errors.porcentajeAnticipo && (
                <p className="text-red-500 text-sm mt-1">{errors.porcentajeAnticipo}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                Comisión (Neto) *
              </label>
              <input
                type="number"
                name="comision"
                value={formData.comision}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.comision ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingrese monto"
                step="0.01"
                min="0"
              />
              {errors.comision && (
                <p className="text-red-500 text-sm mt-1">{errors.comision}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                Gastos operacionales (Neto) *
              </label>
              <input
                type="number"
                name="gastosOperacionales"
                value={formData.gastosOperacionales}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.gastosOperacionales ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingrese monto"
                step="0.01"
                min="0"
              />
              {errors.gastosOperacionales && (
                <p className="text-red-500 text-sm mt-1">{errors.gastosOperacionales}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                Otros gastos
              </label>
              <input
                type="number"
                name="otrosGastos"
                value={formData.otrosGastos}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ingrese monto (opcional)"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Sección Facturas */}
        <div className="seccion-datos mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 style={{color: 'var(--if-primary)', margin: '0', fontSize: '1.2rem', fontWeight: '600'}}>
              Facturas
            </h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowCsvUpload(!showCsvUpload)}
                className="btn-secondary flex items-center gap-2"
              >
                <MdUpload size={16} />
                Cargar CSV
              </button>
              <button
                type="button"
                onClick={addFactura}
                className="btn-primary flex items-center gap-2"
                style={{background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)'}}
              >
                <MdAdd size={16} />
                Agregar Factura
              </button>
            </div>
          </div>

          {showCsvUpload && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="block text-sm font-medium">
                  Cargar facturas desde CSV (NUMERO_FACTURA;DEUDOR;FECHA_VENCIMIENTO;MONTO_FACTURA):
                </label>
                <button
                  type="button"
                  onClick={downloadCsvPlanilla}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <MdDownload size={18} />
                  Descargar planilla
                </button>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          )}

          <div className="space-y-4">
            {facturas.map((factura, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-lg">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    N° Factura
                  </label>
                  <input
                    type="text"
                    value={factura.numeroFactura}
                    onChange={(e) => handleFacturaChange(index, 'numeroFactura', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Número de factura"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Deudor
                  </label>
                  <input
                    type="text"
                    value={factura.deudor}
                    onChange={(e) => handleFacturaChange(index, 'deudor', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nombre del deudor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Fecha Vencimiento
                  </label>
                  <input
                    type="date"
                    value={factura.fechaVencimiento}
                    onChange={(e) => handleFacturaChange(index, 'fechaVencimiento', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-2">
                      Monto Factura
                    </label>
                    <input
                      type="number"
                      value={factura.montoFactura}
                      onChange={(e) => handleFacturaChange(index, 'montoFactura', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Monto"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  {facturas.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFactura(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <MdDelete size={20} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => {
              setFormData({
                nombreCliente: '',
                rutCliente: '',
                fechaOperacion: '',
                tasaInteres: '',
                porcentajeAnticipo: '',
                comision: '',
                gastosOperacionales: '',
                otrosGastos: ''
              });
              setErrors({});
            }}
            className="btn-secondary"
          >
            Limpiar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, var(--if-primary) 0%, var(--if-primary-dark) 100%)',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Procesando Simulación...' : 'Simular Operación'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SimuladorOperaciones;